<script lang="ts">
  // ContainerRow — single row in the Containers table.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // node ID 0:329 (ContainerRow 1200×48, 9 children: checkbox + name +
  // image + state + uptime + cpu + mem + actions).
  //
  // The row is purely presentational — it dispatches a `mutate`
  // event with `{action, container}` when an action button is clicked
  // and a `toggle` event when the checkbox flips. PageContainers wires
  // both: mutate → ActionModal, toggle → selection state.

  import { createEventDispatcher } from "svelte";
  import StateBadge from "./StateBadge.svelte";
  import { formatDuration, formatPercent, formatBytes } from "../lib/format";
  import type { ContainerRowData } from "../lib/bindings";
  import type { IconName } from "../lib/icons";

  export let container: ContainerRowData;
  export let selected: boolean;

  const dispatch = createEventDispatcher<{
    mutate: { action: "restart" | "stop" | "start"; container: string };
    toggle: { container: string; selected: boolean };
  }>();

  // --- action button enabled/disabled logic ------------------------------
  // Matches bollard state semantics: start is only meaningful when the
  // container is not running; stop only when it is running; restart is
  // safe only when running OR when state is restarting/paused (which
  // are not actual failure states).

  // `canStart` is the inverse of running — only meaningful when the
  // container is stopped / exited / dead / created. `canStop` only when
  // running. `canRestart` only when running or paused; restarting and
  // already-restart-loop states are no-ops because a restart is in
  // flight and another click would just queue another.
  $: canStart = !["running"].includes(container.state);
  $: canStop = container.state === "running";
  $: canRestart = ["running", "paused"].includes(container.state);

  function fire(action: "restart" | "stop" | "start"): void {
    dispatch("mutate", { action, container: container.name });
  }

  function onCheckboxChange(e: Event): void {
    const checked = (e.target as HTMLInputElement).checked;
    dispatch("toggle", { container: container.name, selected: checked });
  }

  function iconPath(name: IconName): string {
    // Inline import avoids a circular dep at module-init time.
    // lib/icons.ts is pure data — keeping this in one place keeps the
    // file light and avoids pulling all 24 paths into the bundle
    // before they're needed.
    return ICON_PATHS[name];
  }

  const ICON_PATHS: Record<IconName, string> = {
    restart: "M1 4v6h6M23 20v-6h-6M3.51 15a9 9 0 1 0 2.13-9.36L1 10",
    stop: "M6 6h12v12H6z",
    play: "M5 3l14 9-14 9V3z",
    home: "",
    host: "",
    container: "",
    storage: "",
    services: "",
    network: "",
    audit: "",
    settings: "",
    search: "",
    filter: "",
    refresh: "",
    close: "",
    check: "",
    warning: "",
    error: "",
    info: "",
    clock: "",
    user: "",
    logout: "",
    menu: "",
    "external-link": "",
  };
</script>

<tr class="container-row" data-testid="container-row" data-container={container.name}>
  <td class="container-row__cell container-row__cell--checkbox">
    <input
      type="checkbox"
      class="container-row__checkbox"
      data-testid="row-checkbox"
      aria-label={`Select ${container.name}`}
      checked={selected}
      on:change={onCheckboxChange}
    />
  </td>
  <td class="container-row__cell container-row__cell--name">
    <a
      href={`/containers/${encodeURIComponent(container.name)}`}
      class="container-row__name-link"
      data-testid="row-name-link"
    >
      {container.name}
    </a>
  </td>
  <td class="container-row__cell container-row__cell--image">
    <span class="container-row__image">{container.image}</span>
  </td>
  <td class="container-row__cell container-row__cell--state">
    <StateBadge state={container.state} />
  </td>
  <td class="container-row__cell container-row__cell--uptime mono">
    {formatDuration(Number(container.uptime_secs ?? container.uptime_seconds ?? 0) * 1000)}
  </td>
  <td class="container-row__cell container-row__cell--cpu mono">
    {formatPercent(container.cpu_pct)}
  </td>
  <td class="container-row__cell container-row__cell--mem mono">
    {formatBytes(container.mem_used)}
  </td>
  <td class="container-row__cell container-row__cell--actions">
    <div class="container-row__actions">
      <button
        type="button"
        class="icon-btn"
        data-action="start"
        data-testid="row-action-start"
        aria-label={`Start ${container.name}`}
        disabled={!canStart}
        on:click={() => fire("start")}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
          <path d={iconPath("play")} />
        </svg>
      </button>
      <button
        type="button"
        class="icon-btn"
        data-action="stop"
        data-testid="row-action-stop"
        aria-label={`Stop ${container.name}`}
        disabled={!canStop}
        on:click={() => fire("stop")}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
          <path d={iconPath("stop")} />
        </svg>
      </button>
      <button
        type="button"
        class="icon-btn icon-btn--accent"
        data-action="restart"
        data-testid="row-action-restart"
        aria-label={`Restart ${container.name}`}
        disabled={!canRestart}
        on:click={() => fire("restart")}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d={iconPath("restart")} />
        </svg>
      </button>
    </div>
  </td>
</tr>

<style>
  .container-row {
    display: grid;
    grid-template-columns: 32px 200px 1fr 120px 96px 72px 96px 144px;
    align-items: center;
    gap: var(--space-3);
    height: 48px;
    padding: 0 var(--space-4);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-strong);
    font: var(--type-body);
    transition: background var(--motion-fast) var(--easing-standard);
  }

  .container-row:hover {
    background: var(--surface-2);
  }

  .container-row__cell {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .container-row__checkbox {
    cursor: pointer;
  }

  .container-row__name-link {
    color: var(--text-strong);
    font-weight: 500;
    text-decoration: none;
  }
  .container-row__name-link:hover {
    color: var(--accent-cyan);
  }
  .container-row__name-link:focus-visible {
    outline: 2px solid var(--accent-cyan);
    outline-offset: 2px;
    border-radius: 4px;
  }

  .container-row__image {
    color: var(--text-mid);
    font: var(--type-mono-md);
  }

  .container-row__actions {
    display: inline-flex;
    gap: var(--space-2);
    justify-content: flex-end;
  }

  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    border: 1px solid var(--border);
    color: var(--text-mid);
    cursor: pointer;
    transition:
      background var(--motion-fast) var(--easing-standard),
      color var(--motion-fast) var(--easing-standard);
  }
  .icon-btn:hover:not(:disabled) {
    background: var(--surface-3);
    color: var(--text-strong);
  }
  .icon-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .icon-btn--accent {
    color: var(--accent-cyan);
  }
  .icon-btn--accent:hover:not(:disabled) {
    background: var(--accent-bg);
    color: var(--accent-cyan);
  }
</style>